import React, {useEffect, useMemo, useState} from "react";
import {Button, Descriptions, Form, Input, Modal, Space, Spin, Table, Tabs, Tag} from "antd";
import {message} from "../../utils/antdFeedback";
import {ArrowLeft, CreditCard, Landmark, Link as LinkIcon, ReceiptText, Store, UserCog} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {AppDispatch, RootState} from "../../redux/store";
import {
  activateApplicationUser,
  deactivateApplicationUser,
  deleteApplicationUser,
  getApplicationUserById,
  resendApplicationUserVerification,
  unlockApplicationUserBillPayments,
  unlockApplicationUserLiquidations,
  updateApplicationUser,
} from "../../redux/slice/userManagementSlice";
import {HeroHeader, Card, COLORS, formatNaira} from "../../designSystem";
import {OwnerPaymentLinkBalance, LinkPaymentBalance} from "./PaymentLinkBalances";

const dateText = (value: string) => value ? new Date(value).toLocaleString() : "-";
const text = (value: any) => value || "-";
const money = (value: any, currency = "NGN") =>
  !currency || currency === "NGN" ? formatNaira(value || 0) : `${currency} ${Number(value || 0).toLocaleString()}`;

const totalMoney = (value: any) => value == null ? "—" : money(value);

const tagColor = (status: any) => {
  const value = String(status || "").toLowerCase();
  if (["paid", "successful", "completed", "active", "verified"].includes(value)) return "green";
  if (["failed", "rejected", "deleted", "cancelled", "expired"].includes(value)) return "red";
  if (["pending", "processing", "awaitingtransfer", "verificationqueued", "inactive", "unverified"].includes(value)) return "gold";
  return "blue";
};

const statusTag = (status: any) => <Tag color={tagColor(status)}>{text(status)}</Tag>;

const userStatusTag = (user: any) => {
  if (user?.isDeleted) return <Tag color="red">Deleted</Tag>;
  return user?.isActive ? <Tag color="green">Active</Tag> : <Tag color="orange">Inactive</Tag>;
};

function UserDetails() {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const {token} = useSelector((state: RootState) => state.account);
  const {selectedUser, loading} = useSelector((state: RootState) => state.userManagement);
  const [editOpen, setEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [form] = Form.useForm();
  const isCurrentUser = selectedUser && String(selectedUser.id) === String(id);

  useEffect(() => {
    if (token && id) {
      dispatch(getApplicationUserById({token, userId: id}));
    }
  }, [token, id, dispatch]);

  useEffect(() => {
    if (selectedUser) {
      form.setFieldsValue(selectedUser);
    }
  }, [selectedUser, form]);

  const runAction = async (action: any, success: string) => {
    if (!token || !id) return;
    try {
      await dispatch(action).unwrap();
      message.success(success);
    } catch (error: any) {
      message.error(error?.message || error || "Action failed");
    }
  };

  const save = async (values: any) => {
    if (!token || !id) return;
    try {
      await dispatch(updateApplicationUser({token, userId: id, values})).unwrap();
      message.success("User updated");
      setEditOpen(false);
    } catch (error: any) {
      message.error(error?.message || error || "Could not update user");
    }
  };

  const confirmDeactivate = () => Modal.confirm({
    title: "Deactivate user?",
    content: "The user will no longer be active on the platform.",
    okText: "Deactivate",
    okButtonProps: {danger: true},
    onOk: () => id && runAction(deactivateApplicationUser({token, userId: id}), "User deactivated"),
  });

  const confirmDelete = () => Modal.confirm({
    title: "Delete user?",
    content: "This anonymizes the account, but it can be restored from this admin page.",
    okText: "Delete",
    okButtonProps: {danger: true},
    onOk: () => id && runAction(deleteApplicationUser({token, userId: id}), "User deleted"),
  });

  const confirmActivate = () => Modal.confirm({
    title: selectedUser?.isDeleted ? "Restore user?" : "Activate user?",
    content: selectedUser?.isDeleted ? "The original email will be restored if it is still available." : "The user will be marked active.",
    okText: selectedUser?.isDeleted ? "Restore" : "Activate",
    onOk: () => id && runAction(activateApplicationUser({token, userId: id, email: selectedUser?.displayEmail}), "User activated"),
  });

  const confirmUnlockLiquidations = () => Modal.confirm({
    title: "Unlock liquidations?",
    content: "This resets the liquidation failure counter and allows this user to request or complete liquidation flows again.",
    okText: "Unlock",
    onOk: () => id && runAction(unlockApplicationUserLiquidations({token, userId: id, values: {reason: "Unlocked after admin review"}}), "Liquidations unlocked"),
  });

  const confirmUnlockBillPayments = () => Modal.confirm({
    title: "Unlock bill payments?",
    content: "This resets the bill payment failure counter and allows this user to pay bills again.",
    okText: "Unlock",
    onOk: () => id && runAction(unlockApplicationUserBillPayments({token, userId: id, values: {reason: "Unlocked after admin review"}}), "Bill payments unlocked"),
  });

  const featureLockCard = (
    title: string,
    locked: boolean,
    failureCount: number,
    lastFailedAt: string,
    lockedAt: string,
    reason: string,
    onUnlock: () => void
  ) => (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold" style={{color: COLORS.text}}>{title}</div>
          <div className="mt-2">{locked ? <Tag color="red">Locked</Tag> : <Tag color="green">Unlocked</Tag>}</div>
        </div>
        {locked && <Button onClick={onUnlock}>Unlock</Button>}
      </div>
      <Descriptions className="mt-3" size="small" column={1}>
        <Descriptions.Item label="Failed attempts">{failureCount || 0}</Descriptions.Item>
        <Descriptions.Item label="Last failed">{dateText(lastFailedAt)}</Descriptions.Item>
        <Descriptions.Item label="Locked at">{dateText(lockedAt)}</Descriptions.Item>
        <Descriptions.Item label="Reason">{text(reason)}</Descriptions.Item>
      </Descriptions>
    </Card>
  );

  const overviewCards = useMemo(() => ([
    {key: "wallet", label: "Wallet Balance", value: money(selectedUser?.walletBalance), detail: `${selectedUser?.walletCount ?? "—"} wallets`, icon: CreditCard},
    {key: "groups", label: "Group Savings", value: selectedUser?.groupSavingsCount || 0, detail: `Group active total: ${totalMoney(selectedUser?.groupSavingsTotalActiveBalance)}`, icon: Landmark},
    {key: "paymentLinks", label: "Payment Links", value: selectedUser?.paymentLinkCount || 0,
      detail: `Collected: ${totalMoney(selectedUser?.paymentLinkBalance?.bookBalance)}`,
      balance: `Available after payouts: ${totalMoney(selectedUser?.paymentLinkBalance?.actualBalance)}`,
      paidOut: `Paid out: ${totalMoney(selectedUser?.paymentLinkBalance?.liquidatedAmount)}`,
      pending: `Pending payouts: ${totalMoney(selectedUser?.paymentLinkBalance?.pendingLiquidationAmount)}`, icon: LinkIcon},
    {key: "bills", label: "Bill Payments", value: selectedUser?.billPaymentCount || 0, detail: `Successful total: ${totalMoney(selectedUser?.billPaymentTotalAmount)}`, icon: ReceiptText},
    {key: "merchants", label: "Merchants", value: selectedUser?.merchantCount ?? selectedUser?.merchants?.length ?? 0, detail: `Merchant collections: ${totalMoney(selectedUser?.merchantTotalCollected)}`, icon: Store},
  ]), [selectedUser]);

  if (loading || !isCurrentUser) {
    return <AdminLayout><div className="m-10"><Spin tip="Loading user..."/></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={UserCog}
          title={selectedUser ? `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim() || selectedUser.displayEmail : "User Details"}
          subtitle={selectedUser?.displayEmail || selectedUser?.email}
          actions={
            <div className="flex flex-wrap gap-2">
              <Button icon={<ArrowLeft size={16}/>} onClick={() => navigate(-1)}>Back</Button>
              <Button onClick={() => setEditOpen(true)} disabled={selectedUser?.isDeleted}>Edit</Button>
              <Button onClick={() => id && runAction(resendApplicationUserVerification({token, userId: id}), "Verification email sent")} disabled={selectedUser?.isDeleted || selectedUser?.emailConfirmed}>Resend verification</Button>
              {selectedUser?.isActive && !selectedUser?.isDeleted ? (
                <Button danger onClick={confirmDeactivate}>Deactivate</Button>
              ) : (
                <Button onClick={confirmActivate}>{selectedUser?.isDeleted ? "Restore" : "Activate"}</Button>
              )}
              {!selectedUser?.isDeleted && <Button danger onClick={confirmDelete}>Delete</Button>}
            </div>
          }
        />

        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5 mt-8">
          {overviewCards.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.key} onClick={() => setActiveTab(item.key)} className="text-left">
                <Card className="p-5 transition hover:shadow-md">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm text-slate-500">{item.label}</div>
                    <Icon size={18} color={COLORS.brand}/>
                  </div>
                  <div className="mt-2 text-2xl font-bold font-mono" style={{color: item.key === "wallet" ? COLORS.brand : COLORS.text}}>{item.value}</div>
                  <div className="mt-2 text-xs text-slate-500 break-words">{item.detail}</div>
                  {item.balance && <div className="mt-1 text-xs font-medium" style={{color: COLORS.brand}}>{item.balance}</div>}
                  {item.paidOut && <div className="mt-1 text-xs text-slate-500">{item.paidOut}</div>}
                  {item.pending && <div className="mt-1 text-xs text-slate-500">{item.pending}</div>}
                </Card>
              </button>
            );
          })}
        </div>

        <Card className="mt-8 p-5">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "overview",
                label: "Overview",
                children: (
                  <Space direction="vertical" size={16} className="w-full">
                    <div className="grid gap-4 md:grid-cols-2">
                      {featureLockCard(
                        "Liquidations",
                        selectedUser?.liquidationCircuitBreakerLocked,
                        selectedUser?.liquidationCircuitBreakerFailureCount,
                        selectedUser?.liquidationCircuitBreakerLastFailedAt,
                        selectedUser?.liquidationCircuitBreakerLockedAt,
                        selectedUser?.liquidationCircuitBreakerReason,
                        confirmUnlockLiquidations
                      )}
                      {featureLockCard(
                        "Bill payments",
                        selectedUser?.billPaymentCircuitBreakerLocked,
                        selectedUser?.billPaymentCircuitBreakerFailureCount,
                        selectedUser?.billPaymentCircuitBreakerLastFailedAt,
                        selectedUser?.billPaymentCircuitBreakerLockedAt,
                        selectedUser?.billPaymentCircuitBreakerReason,
                        confirmUnlockBillPayments
                      )}
                    </div>
                    <Descriptions bordered column={1}>
                      <Descriptions.Item label="Status">{userStatusTag(selectedUser)}</Descriptions.Item>
                      <Descriptions.Item label="Email Verification">{selectedUser?.emailConfirmed ? <Tag color="green">Verified</Tag> : <Tag color="orange">Unverified</Tag>}</Descriptions.Item>
                      <Descriptions.Item label="Email">{text(selectedUser?.displayEmail || selectedUser?.email)}</Descriptions.Item>
                      <Descriptions.Item label="Phone">{text(selectedUser?.phoneNumber)}</Descriptions.Item>
                      <Descriptions.Item label="Registered">{dateText(selectedUser?.dateRegistered)}</Descriptions.Item>
                      <Descriptions.Item label="Deleted At">{dateText(selectedUser?.deletedAt)}</Descriptions.Item>
                      <Descriptions.Item label="Registration Source">{text(selectedUser?.registrationSource)}</Descriptions.Item>
                      <Descriptions.Item label="Soft Registered">{selectedUser?.isSoftRegistered ? "Yes" : "No"}</Descriptions.Item>
                      <Descriptions.Item label="Profile Completed">{selectedUser?.profileCompleted ? "Yes" : "No"}</Descriptions.Item>
                      <Descriptions.Item label="Bank">{text(selectedUser?.bankName)}</Descriptions.Item>
                      <Descriptions.Item label="Bank Account">{text(selectedUser?.bankAccountNumber)}</Descriptions.Item>
                      <Descriptions.Item label="Bank Code">{text(selectedUser?.bankCode)}</Descriptions.Item>
                      <Descriptions.Item label="BVN Verified">{selectedUser?.bvnVerified ? "Yes" : "No"}</Descriptions.Item>
                      <Descriptions.Item label="Address">{text(selectedUser?.address)}</Descriptions.Item>
                      <Descriptions.Item label="Residential Address">{text(selectedUser?.residentialAddress)}</Descriptions.Item>
                      <Descriptions.Item label="Office Address">{text(selectedUser?.officeAddress)}</Descriptions.Item>
                    </Descriptions>
                  </Space>
                ),
              },
              {
                key: "wallet",
                label: "Wallet",
                children: (
                  <Space direction="vertical" size={18} className="w-full">
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={{pageSize: 10}}
                      dataSource={selectedUser?.walletTransactions || []}
                      columns={[
                        {title: "Reference", dataIndex: "reference"},
                        {title: "Type", dataIndex: "transactionType"},
                        {title: "Amount", dataIndex: "amount", render: (value: any) => money(value)},
                        {title: "Status", dataIndex: "status", render: statusTag},
                        {title: "Description", dataIndex: "description"},
                        {title: "Date", dataIndex: "transactionDate", render: dateText},
                      ]}
                    />
                    <Table
                      rowKey="id"
                      size="small"
                      pagination={{pageSize: 10}}
                      title={() => "VFD virtual accounts and funding instructions"}
                      dataSource={selectedUser?.virtualAccounts || []}
                      columns={[
                        {title: "Reference", dataIndex: "reference"},
                        {title: "Account", render: (_: any, row: any) => `${text(row.accountName)} · ${text(row.accountNumber)}`},
                        {title: "Bank", dataIndex: "bankName"},
                        {title: "Amount", dataIndex: "transactionAmount", render: (value: any) => money(value)},
                        {title: "Status", dataIndex: "status", render: statusTag},
                        {title: "Created", dataIndex: "initiatedAt", render: dateText},
                        {title: "Expires", dataIndex: "expiresAt", render: dateText},
                      ]}
                    />
                  </Space>
                ),
              },
              {
                key: "groups",
                label: "Group Savings",
                children: (
                  <Table
                    rowKey="id"
                    dataSource={selectedUser?.groupSavings || []}
                    pagination={{pageSize: 10}}
                    expandable={{
                      expandedRowRender: (group: any) => (
                        <Space direction="vertical" size={16} className="w-full">
                          <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Account">{text(group.accountName)} {text(group.accountNumber)}</Descriptions.Item>
                            <Descriptions.Item label="Bank">{text(group.bankName)}</Descriptions.Item>
                            <Descriptions.Item label="Book Balance">{money(group.bookBalance)}</Descriptions.Item>
                            <Descriptions.Item label="Active Balance">{money(group.activeBalance)}</Descriptions.Item>
                            <Descriptions.Item label="Role">{group.isAdmin ? "Admin" : group.isValidator ? "Validator" : "Member"}</Descriptions.Item>
                            <Descriptions.Item label="Joined">{dateText(group.dateJoined)}</Descriptions.Item>
                          </Descriptions>
                          <Table
                            rowKey="userId"
                            size="small"
                            pagination={false}
                            title={() => "Members"}
                            dataSource={group.members || []}
                            columns={[
                              {title: "Name", dataIndex: "fullName", render: text},
                              {title: "Email", dataIndex: "email"},
                              {title: "Role", dataIndex: "role"},
                              {title: "Status", dataIndex: "status", render: statusTag},
                              {title: "Joined", dataIndex: "dateJoined", render: dateText},
                            ]}
                          />
                          <Table
                            rowKey="id"
                            size="small"
                            pagination={{pageSize: 5}}
                            title={() => "User payments in this group"}
                            dataSource={group.payments || []}
                            columns={[
                              {title: "Category", dataIndex: "categoryName"},
                              {title: "Type", dataIndex: "paymentType"},
                              {title: "Frequency", dataIndex: "paymentFrequency"},
                              {title: "Amount", dataIndex: "amountPaid", render: (value: any) => money(value)},
                              {title: "Reference", dataIndex: "paymentReference", render: text},
                              {title: "Status", dataIndex: "status", render: statusTag},
                              {title: "Date", dataIndex: "datePaid", render: dateText},
                            ]}
                          />
                        </Space>
                      ),
                    }}
                    columns={[
                      {title: "Group", dataIndex: "name"},
                      {title: "Book Balance", dataIndex: "bookBalance", render: (value: any) => money(value)},
                      {title: "Active Balance", dataIndex: "activeBalance", render: (value: any) => money(value)},
                      {title: "Status", dataIndex: "status", render: statusTag},
                      {title: "Created", dataIndex: "dateCreated", render: dateText},
                    ]}
                  />
                ),
              },
              {
                key: "paymentLinks",
                label: "Payment Links",
                children: (
                  <Space direction="vertical" size={16} className="w-full">
                  <OwnerPaymentLinkBalance balance={selectedUser?.paymentLinkBalance}/>
                  <Table
                    rowKey="id"
                    dataSource={selectedUser?.paymentLinks || []}
                    pagination={{pageSize: 10}}
                    expandable={{
                      expandedRowRender: (link: any) => (
                        <Space direction="vertical" size={16} className="w-full">
                        <LinkPaymentBalance link={link}/>
                        <Table
                          rowKey="id"
                          size="small"
                          pagination={{pageSize: 10}}
                          scroll={{x: "max-content"}}
                          dataSource={link.payments || []}
                          columns={[
                            {title: "Payer", render: (_: any, row: any) => text(row.payerName || row.payerEmail || row.payerPhone)},
                            {title: "Amount", dataIndex: "amount", render: (_: any, row: any) => money(row.amount, row.currency)},
                            {title: "Charge", dataIndex: "chargeAmount", render: (value: any) => money(value)},
                            {title: "Balance restored", render: (_: any, row: any) => row.adminBalanceRefunded && row.status === "Failed" ? money(row.adminBalanceRefundAmount) : "—"},
                            {title: "Status", dataIndex: "status", render: (value: any, row: any) => <div>
                              {statusTag(value)}
                              {value !== "Paid" && row.lastVerificationError && <div className="mt-1 max-w-xs whitespace-normal text-xs text-slate-500">{row.lastVerificationError}</div>}
                            </div>},
                            {title: "Verification attempts", dataIndex: "verificationAttempts"},
                            {title: "VFD Ref", dataIndex: "vfdReference", render: text},
                            {title: "Virtual Account", render: (_: any, row: any) => `${text(row.vfdAccountName)} · ${text(row.vfdAccountNumber)}`},
                            {title: "Created", dataIndex: "createdAt", render: dateText},
                            {title: "Paid", dataIndex: "paidAt", render: dateText},
                          ]}
                        />
                        </Space>
                      ),
                    }}
                    columns={[
                      {title: "Title", dataIndex: "title"},
                      {title: "Type", dataIndex: "type"},
                      {title: "Reference", dataIndex: "reference"},
                      {title: "Amount", render: (_: any, row: any) => row.fixedAmount ? money(row.fixedAmount, row.currency) : "Custom"},
                      {title: "Status", dataIndex: "status", render: statusTag},
                      {title: "Created", dataIndex: "createdAt", render: dateText},
                    ]}
                  />
                  </Space>
                ),
              },
              {
                key: "bills",
                label: "Bills",
                children: (
                  <Table
                    rowKey="id"
                    dataSource={selectedUser?.billPayments || []}
                    pagination={{pageSize: 10}}
                    expandable={{
                      expandedRowRender: (bill: any) => (
                        <Descriptions bordered size="small" column={2}>
                          <Descriptions.Item label="Reference">{text(bill.reference)}</Descriptions.Item>
                          <Descriptions.Item label="Source">{text(bill.source)}</Descriptions.Item>
                          <Descriptions.Item label="Provider Status">{text(bill.providerStatus)}</Descriptions.Item>
                          <Descriptions.Item label="Provider Message">{text(bill.providerMessage)}</Descriptions.Item>
                          <Descriptions.Item label="Failure Reason">{text(bill.failureReason)}</Descriptions.Item>
                          <Descriptions.Item label="Token">{text(bill.token)}</Descriptions.Item>
                          <Descriptions.Item label="VFD Account">{text(bill.vfdAccountName)} {text(bill.vfdAccountNumber)}</Descriptions.Item>
                          <Descriptions.Item label="VFD Bank">{text(bill.vfdBankName)}</Descriptions.Item>
                          <Descriptions.Item label="Last Checked">{dateText(bill.lastCheckedAt)}</Descriptions.Item>
                        </Descriptions>
                      ),
                    }}
                    columns={[
                      {title: "Reference", dataIndex: "reference"},
                      {title: "Biller", dataIndex: "billerName"},
                      {title: "Item", dataIndex: "paymentItemName"},
                      {title: "Customer", dataIndex: "customerId"},
                      {title: "Amount", dataIndex: "amount", render: (value: any) => money(value)},
                      {title: "Source", dataIndex: "source"},
                      {title: "Status", dataIndex: "status", render: statusTag},
                      {title: "Created", dataIndex: "createdAt", render: dateText},
                    ]}
                  />
                ),
              },
              {
                key: "merchants",
                label: "Merchants",
                children: (
                  /*
                    The merchants this user is a member of, each opening to the sub-merchants it
                    onboarded as a platform. The user payload carries the whole tree, so nothing
                    is fetched here.
                  */
                  <Table
                    rowKey="id"
                    dataSource={selectedUser?.merchants || []}
                    pagination={false}
                    locale={{emptyText: "This user is not a member of any merchant."}}
                    expandable={{
                      rowExpandable: (merchant: any) => (merchant.subMerchants?.length || 0) > 0,
                      expandedRowRender: (merchant: any) => (
                        <Table
                          rowKey="id"
                          size="small"
                          pagination={false}
                          dataSource={merchant.subMerchants || []}
                          columns={[
                            {title: "Sub-merchant", dataIndex: "businessName"},
                            {title: "Email", dataIndex: "businessEmail"},
                            {title: "Status", dataIndex: "status", render: statusTag},
                            {title: "Platform Reference", dataIndex: "externalReference", render: text},
                            {
                              title: "Payouts",
                              dataIndex: "payoutsOnHold",
                              render: (onHold: boolean) =>
                                onHold ? <Tag color="red">On hold</Tag> : <Tag color="green">Open</Tag>,
                            },
                            {title: "Created", dataIndex: "createdAt", render: dateText},
                          ]}
                        />
                      ),
                    }}
                    columns={[
                      {
                        title: "Merchant",
                        key: "businessName",
                        render: (_: any, merchant: any) => (
                          <div>
                            <div className="font-medium">{merchant.businessName}</div>
                            <div className="text-xs text-slate-500">{merchant.businessEmail}</div>
                            {merchant.platformBusinessName && (
                              <div className="text-xs" style={{color: COLORS.brand}}>under {merchant.platformBusinessName}</div>
                            )}
                          </div>
                        ),
                      },
                      {title: "Their Role", dataIndex: "role", render: (role: string) => <Tag>{text(role)}</Tag>},
                      {
                        title: "Membership",
                        dataIndex: "membershipIsActive",
                        render: (active: boolean) =>
                          <Tag color={active ? "green" : "red"}>{active ? "Active" : "Inactive"}</Tag>,
                      },
                      {title: "Merchant Status", dataIndex: "status", render: statusTag},
                      {title: "Environment", dataIndex: "currentEnvironment", render: (value: any) => <Tag>{text(value)}</Tag>},
                      {
                        title: "Sub-merchants",
                        key: "subMerchants",
                        render: (_: any, merchant: any) => merchant.subMerchants?.length
                          ? <Tag color="blue">{merchant.subMerchants.length}</Tag>
                          : <span className="text-slate-400">—</span>,
                      },
                      {
                        title: "Payouts",
                        dataIndex: "payoutsOnHold",
                        render: (onHold: boolean) =>
                          onHold ? <Tag color="red">On hold</Tag> : <Tag color="green">Open</Tag>,
                      },
                      {title: "Member Since", dataIndex: "memberSince", render: dateText},
                      {
                        title: "Action",
                        key: "action",
                        render: () => <Button onClick={() => navigate("/merchants")}>Merchants</Button>,
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        </Card>

        <Modal
          title="Edit user"
          open={editOpen}
          onCancel={() => setEditOpen(false)}
          onOk={() => form.submit()}
          confirmLoading={loading}
          width={760}
        >
          <Form form={form} layout="vertical" onFinish={save}>
            <div className="grid gap-3 md:grid-cols-2">
              <Form.Item label="Email" name="email" rules={[{required: true, type: "email"}]}><Input/></Form.Item>
              <Form.Item label="Phone" name="phoneNumber"><Input/></Form.Item>
              <Form.Item label="First Name" name="firstName"><Input/></Form.Item>
              <Form.Item label="Last Name" name="lastName"><Input/></Form.Item>
              <Form.Item label="Bank Name" name="bankName"><Input/></Form.Item>
              <Form.Item label="Bank Code" name="bankCode"><Input/></Form.Item>
              <Form.Item label="Bank Account Number" name="bankAccountNumber"><Input/></Form.Item>
              <Form.Item label="Address" name="address"><Input/></Form.Item>
              <Form.Item label="Residential Address" name="residentialAddress"><Input/></Form.Item>
              <Form.Item label="Office Address" name="officeAddress"><Input/></Form.Item>
            </div>
          </Form>
        </Modal>
      </main>
    </AdminLayout>
  );
}

export default UserDetails;
